/**
 * QBAudit — re-extract supplier invoices from ap@ Gmail for a chosen window,
 * match against real QuickBooks Bills, and bulk-push the missing ones.
 *
 * Lives in its own table (qb_audit_invoices) so the production Expenses
 * page never sees these rows. The push reuses /api/quickbooks-bill?table=
 * qb_audit_invoices so categories/taxes/PDF-attach behaviour is identical
 * to the live flow.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/qb-audit";

const STATUS_META = {
  pending:  { color: "#9ca3af", icon: "•",   label: "Pending"   },
  green:    { color: "#22c55e", icon: "🟢", label: "Matched"   },
  orange:   { color: "#f59e0b", icon: "🟠", label: "Close"     },
  red:      { color: "#ef4444", icon: "🔴", label: "Missing"   },
  pushed:   { color: "#60a5fa", icon: "⬆",  label: "Pushed"    },
  error:    { color: "#a78bfa", icon: "✗",  label: "Error"     },
};
const FILTERS = ["all", "green", "orange", "red", "pushed", "error"];

const today = () => new Date().toISOString().slice(0, 10);

// QB transaction URL — Bills and VendorCredits live at different paths.
function qbTxnUrl(txnType, id) {
  if (!id) return null;
  const path = (txnType === "VendorCredit") ? "vendorcredit" : "bill";
  return `https://app.qbo.intuit.com/app/${path}?txnId=${id}`;
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}
function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}

// Pre-built week buttons covering Apr 1 → today (mid-Apr Dext cancel + audit lookback).
function weekRanges(startISO, endISO) {
  const out = [];
  const start = new Date(startISO + "T00:00:00Z");
  const end   = new Date(endISO   + "T00:00:00Z");
  let cursor = start;
  while (cursor <= end) {
    const weekEnd = new Date(cursor);
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
    if (weekEnd > end) weekEnd.setTime(end.getTime());
    out.push({
      start: cursor.toISOString().slice(0, 10),
      end:   weekEnd.toISOString().slice(0, 10),
    });
    cursor = new Date(weekEnd);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

export default function QBAudit() {
  const [runs, setRuns]                 = useState([]);
  const [activeRunId, setActiveRunId]   = useState(null);
  const [invoices, setInvoices]         = useState([]);
  const [filter, setFilter]             = useState("all");
  const [loadingRuns, setLoadingRuns]   = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [busy, setBusy]                 = useState("");      // "starting" | "extracting" | "matching" | "pushing" | ""
  const [chunkProgress, setChunkProgress] = useState(null);  // { processed, remaining, errors }
  const [errorMsg, setErrorMsg]         = useState("");
  const [statusMsg, setStatusMsg]       = useState("");
  const [qbAccounts, setQbAccounts]     = useState([]);
  const [defaultAccountId, setDefaultAccountId] = useState("");
  const [confirmBulk, setConfirmBulk]   = useState(false);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [drawerId, setDrawerId]         = useState(null);  // audit invoice id whose detail drawer is open
  const [searchTerm, setSearchTerm]     = useState("");
  const [sortKey, setSortKey]           = useState("invoice_date");
  const [sortDir, setSortDir]           = useState("asc");
  const [dupePreview, setDupePreview]   = useState(null);   // { groups, total_groups, total_to_delete } | null
  const [coverageInfo, setCoverageInfo] = useState(null);   // { gmail_count, truly_missing, missing_message_ids, ... }
  const [syncResult, setSyncResult]     = useState(null);   // { audit_pushed, patched, no_match, samples }
  const [stmtPreview, setStmtPreview]   = useState(null);   // { groups, total_to_delete } | null
  const [ocPreview, setOcPreview]       = useState(null);   // order-confirmation cleanup preview
  const [showNewRun, setShowNewRun]     = useState(false);
  const [newRunStart, setNewRunStart]   = useState("2026-04-01");
  const [newRunEnd, setNewRunEnd]       = useState(today());

  const cancelRef = useRef(false);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const r = await apiFetch(`${API}?action=list-runs`).then(x => x.json());
      const list = r.runs || [];
      setRuns(list);
      if (list.length && !activeRunId) setActiveRunId(list[0].id);
    } catch (e) {
      setErrorMsg(`Failed to load runs: ${e.message}`);
    }
    setLoadingRuns(false);
  }, [activeRunId]);

  const loadInvoices = useCallback(async (runId) => {
    if (!runId) { setInvoices([]); return; }
    setLoadingInvoices(true);
    try {
      const r = await apiFetch(`${API}?action=list-invoices&run_id=${runId}`).then(x => x.json());
      setInvoices(r.invoices || []);
    } catch (e) {
      setErrorMsg(`Failed to load invoices: ${e.message}`);
    }
    setLoadingInvoices(false);
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadInvoices(activeRunId); }, [activeRunId, loadInvoices]);
  useEffect(() => {
    apiFetch("/api/quickbooks-accounts?action=accounts")
      .then(r => r.json()).then(d => {
        const accts = d.accounts || [];
        setQbAccounts(accts);
        if (accts.length && !defaultAccountId) setDefaultAccountId(accts[0].id);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeRun = runs.find(r => r.id === activeRunId) || null;

  // ── Run lifecycle: start → extract chunks → match ────────────────────────
  async function startRun(start_date, end_date) {
    setErrorMsg(""); setStatusMsg("");
    setBusy("starting");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "start", start_date, end_date }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "start failed");
      await loadRuns();
      setActiveRunId(r.run.id);
      setStatusMsg(
        `Run #${r.run.id}: ${r.queued} email(s) queued` +
        (r.skipped_already_extracted ? `, ${r.skipped_already_extracted} skipped (already extracted)` : "")
      );
      setShowNewRun(false);
      // Auto-kick extraction so the user doesn't need a second click —
      // runChunk loops until the queue drains and then auto-runs match.
      if (r.queued > 0) {
        await runChunk(r.run.id);
      }
    } catch (e) {
      setErrorMsg(e.message);
    }
    setBusy("");
  }

  // Loops chunks until the queue drains or the user hits Pause. Default is
  // auto-continue; the caller can pass {single:true} to do exactly one chunk.
  // (Earlier this read `autoContinue` from React state inside the closure,
  // which captured the stale value at call-time — fixed by using cancelRef
  // and a parameter instead.)
  async function runChunk(runId, opts = {}) {
    const { single = false, autoMatchOnDone = true } = opts;
    cancelRef.current = false;
    setBusy("extracting");
    let totalProcessed = 0;
    let totalErrors = 0;
    let lastResp = null;
    try {
      while (!cancelRef.current) {
        const r = await apiFetch(API, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "extract-chunk", run_id: runId, limit: 8 }),
        }).then(x => x.json());
        if (!r.ok) throw new Error(r.error || "extract-chunk failed");
        lastResp = r;
        totalProcessed += r.processed_in_chunk || 0;
        totalErrors    += r.errors || 0;
        setChunkProgress({
          processed: totalProcessed,
          remaining: r.remaining,
          errors: totalErrors,
        });
        // Refresh the table after each chunk so the user sees rows appear live.
        loadInvoices(runId);
        if (r.done) break;
        if (single) break;
      }
      await loadRuns();
      if (lastResp?.done) {
        setStatusMsg(`Extraction complete — ${totalProcessed} processed, ${totalErrors} errors. Matching against QuickBooks…`);
      } else if (cancelRef.current) {
        setStatusMsg(`Paused — ${lastResp?.remaining ?? "?"} email(s) still queued. Click Continue to resume.`);
      }
    } catch (e) {
      setErrorMsg(`Extraction error: ${e.message}`);
    }
    setBusy("");
    setChunkProgress(null);

    // Auto-run match when the queue is empty so the user doesn't end up
    // staring at a screenful of "pending" rows wondering what's wrong.
    if (autoMatchOnDone && lastResp?.done && !cancelRef.current) {
      await matchRun(runId);
    }
  }

  async function matchRun(runId) {
    setBusy("matching"); setErrorMsg(""); setStatusMsg("");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "match", run_id: runId }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "match failed");
      setStatusMsg(`Matched — 🟢 ${r.green || 0} · 🟠 ${r.orange || 0} · 🔴 ${r.red || 0} (against ${r.qb_bills} QB Bills)`);
      await loadRuns();
      await loadInvoices(runId);
    } catch (e) {
      setErrorMsg(`Match error: ${e.message}`);
    }
    setBusy("");
  }

  async function retryFailed(runId) {
    setBusy("extracting");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "retry-failed", run_id: runId }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error);
      setStatusMsg(`Re-queued ${r.requeued} failed email(s). Running…`);
      await loadInvoices(runId);
      await runChunk(runId);
    } catch (e) {
      setErrorMsg(e.message);
      setBusy("");
    }
  }

  async function deleteRun(runId) {
    if (!window.confirm("Delete this run and all its rows? This is permanent.")) return;
    try {
      await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-run", run_id: runId }),
      });
      setActiveRunId(null);
      await loadRuns();
    } catch (e) { setErrorMsg(e.message); }
  }

  // ── Push to QuickBooks (single + bulk) ────────────────────────────────────
  async function pushOne(inv) {
    const accountId = inv.qb_account_id || defaultAccountId;
    const accountName = inv.qb_account_name || (qbAccounts.find(a => a.id === accountId)?.name) || "";
    if (!accountId) { setErrorMsg("No QB account auto-assigned and no default set"); return; }

    setBusy("pushing");
    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "publish",
          invoiceId: inv.id,
          accountId,
          accountName,
          table: "qb_audit_invoices",
        }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "publish failed");
      // Mark this row as pushed locally + reload from server
      setInvoices(prev => prev.map(i => i.id === inv.id
        ? { ...i, qb_published: true, qb_bill_id: r.billId, match_status: "pushed" }
        : i));
      // Persist the match_status flip server-side so reloads keep it
      await apiFetch(`${API}?action=list-invoices&run_id=${activeRunId}`); // refresh
      setStatusMsg(`✓ Pushed Bill #${r.billId} (${r.vendor})`);
    } catch (e) {
      setErrorMsg(`Push error: ${e.message}`);
    }
    setBusy("");
  }

  async function pushBulk(ids) {
    if (!ids.length) { setErrorMsg("No invoices selected"); return; }
    if (!defaultAccountId) {
      setErrorMsg("Pick a default QB account first");
      return;
    }
    const acct = qbAccounts.find(a => a.id === defaultAccountId);
    setBusy("pushing");
    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "bulk-publish",
          invoiceIds: ids,
          accountId: defaultAccountId,
          accountName: acct?.name || "",
          table: "qb_audit_invoices",
        }),
      }).then(x => x.json());
      setStatusMsg(`Bulk push: ${r.succeeded || 0} pushed, ${r.failed || 0} failed`);
      await loadInvoices(activeRunId);
      setSelectedIds(new Set());
      setConfirmBulk(false);
    } catch (e) {
      setErrorMsg(`Bulk push error: ${e.message}`);
    }
    setBusy("");
  }

  async function markInQb(invoice, qbBillId) {
    if (!window.confirm(`Mark this invoice as already in QuickBooks${qbBillId ? ` (Bill #${qbBillId})` : ""}?\n\nThis will NOT create a Bill in QB — it just flips the audit status to "pushed" so you don't accidentally push a duplicate.`)) return;
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "mark-in-qb", invoice_id: invoice.id, qb_bill_id: qbBillId || null }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "mark-in-qb failed");
      setInvoices(prev => prev.map(i => i.id === invoice.id
        ? { ...i, match_status: "pushed", qb_published: true, qb_bill_id: qbBillId || null }
        : i));
      setStatusMsg(`Marked invoice as already in QB${qbBillId ? ` (Bill #${qbBillId})` : ""}.`);
    } catch (e) { setErrorMsg(e.message); }
  }

  async function bulkDeleteSelected() {
    if (selectedIds.size === 0) { setErrorMsg("No rows selected"); return; }
    const ids = [...selectedIds];
    if (!window.confirm(`Delete ${ids.length} selected audit row(s)? This is permanent. Already-pushed rows will be skipped automatically.`)) return;
    setBusy("deleting");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "bulk-delete", invoice_ids: ids }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "bulk-delete failed");
      setStatusMsg(`Deleted ${r.deleted} row(s)${r.skipped_pushed ? ` · ${r.skipped_pushed} skipped (already pushed to QB)` : ""}`);
      setSelectedIds(new Set());
      await loadInvoices(activeRunId);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function deleteInvoice(invoiceId) {
    if (!window.confirm("Delete this audit row? This is permanent (the original Gmail email is not affected).")) return;
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-invoice", invoice_id: invoiceId }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "delete failed");
      setInvoices(prev => prev.filter(i => i.id !== invoiceId));
      if (drawerId === invoiceId) setDrawerId(null);
    } catch (e) { setErrorMsg(e.message); }
  }

  async function verifyCoverage() {
    if (!activeRunId) return;
    setBusy("verify");
    setCoverageInfo(null);
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "verify-coverage", run_id: activeRunId }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "verify-coverage failed");
      setCoverageInfo(r);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  // Sync qb_published / qb_bill_id from qb_audit_invoices back to matching
  // pending_invoices rows. Fixes Expense-tab rows that show "Push to QB" but
  // were already pushed via QB Audit (and didn't mirror because there was no
  // source_pending_id link).
  async function syncPendingStatus() {
    if (busy) return;
    setBusy("sync-pending");
    setSyncResult(null);
    setErrorMsg("");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync-pending-status" }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "sync-pending-status failed");
      setSyncResult(r);
      setStatusMsg(
        r.patched > 0
          ? `Synced ${r.patched} pending invoice(s) — bill #s now show on the Expenses tab.`
          : "Nothing to sync — every audit-pushed invoice is already reflected on the Expenses tab."
      );
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function requeueMissing(messageIds) {
    if (!activeRunId || !messageIds?.length) return;
    setBusy("verify");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "requeue-missing", run_id: activeRunId, message_ids: messageIds }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "requeue failed");
      setStatusMsg(`Re-queued ${r.queued} missing email(s). Running extraction…`);
      setCoverageInfo(null);
      await loadRuns();
      await runChunk(activeRunId);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function previewOrderConfCleanup() {
    if (!activeRunId) return;
    setBusy("ordconf");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cleanup-order-confirmations", run_id: activeRunId, mode: "preview" }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "preview failed");
      if ((r.total_to_delete || 0) === 0) {
        setStatusMsg("No order-confirmation rows found in this run.");
      } else {
        setOcPreview(r);
      }
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function applyOrderConfCleanup() {
    if (!activeRunId) return;
    setBusy("ordconf");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cleanup-order-confirmations", run_id: activeRunId, mode: "apply" }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "cleanup failed");
      setStatusMsg(`Removed ${r.deleted} order-confirmation row(s). Re-match to refresh status counts.`);
      setOcPreview(null);
      await loadInvoices(activeRunId);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function previewStatementCleanup() {
    if (!activeRunId) return;
    setBusy("statements");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cleanup-statements", run_id: activeRunId, mode: "preview" }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "preview failed");
      if ((r.total_to_delete || 0) === 0) {
        setStatusMsg("No statement-derived rows found in this run.");
      } else {
        setStmtPreview(r);
      }
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function applyStatementCleanup() {
    if (!activeRunId) return;
    setBusy("statements");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cleanup-statements", run_id: activeRunId, mode: "apply" }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "cleanup failed");
      setStatusMsg(`Removed ${r.deleted} statement-derived row(s). Re-match to refresh status counts.`);
      setStmtPreview(null);
      await loadInvoices(activeRunId);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function findDuplicates() {
    if (!activeRunId) return;
    setBusy("dedupe");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "find-duplicates", run_id: activeRunId }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "find-duplicates failed");
      setDupePreview(r);
      if ((r.total_groups || 0) === 0) {
        setStatusMsg("No duplicates found.");
        setDupePreview(null);
      }
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  async function applyDedupe(idsToDelete) {
    setBusy("dedupe");
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "dedupe", run_id: activeRunId, ids_to_delete: idsToDelete }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "dedupe failed");
      setStatusMsg(`Removed ${r.deleted} duplicate row(s)${r.skipped_pushed ? ` · ${r.skipped_pushed} skipped (already pushed to QB)` : ""}`);
      setDupePreview(null);
      await loadInvoices(activeRunId);
    } catch (e) { setErrorMsg(e.message); }
    setBusy("");
  }

  // Persist a category change for one audit row. Optimistic local update +
  // backend write; reverts on failure.
  async function updateCategory(invoiceId, accountId, accountName) {
    const prev = invoices;
    setInvoices(p => p.map(i => i.id === invoiceId
      ? { ...i, qb_account_id: accountId || null, qb_account_name: accountName || null }
      : i));
    try {
      const r = await apiFetch(API, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update-category",
          invoice_id: invoiceId,
          qb_account_id: accountId || null,
          qb_account_name: accountName || null,
        }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || "update-category failed");
    } catch (e) {
      setInvoices(prev);
      setErrorMsg(`Category save failed: ${e.message}`);
    }
  }

  // ── Run stats (computed locally from invoices) ───────────────────────────
  const stats = (() => {
    if (!invoices.length) return null;
    const byStatus = { green: 0, orange: 0, red: 0, pushed: 0, error: 0, pending: 0 };
    const byStatus$ = { green: 0, orange: 0, red: 0, pushed: 0, error: 0, pending: 0 };
    const supplierMap = new Map();
    let usableTotal = 0;     // sum of total $ across all rows that have a real invoice
    let withTax = 0;         // rows with gst+pst > 0
    let taxExempt = 0;
    for (const i of invoices) {
      const s = i.match_status || "pending";
      byStatus[s] = (byStatus[s] || 0) + 1;
      const t = Number(i.total || 0);
      byStatus$[s] = (byStatus$[s] || 0) + t;
      if (i.supplier && i.invoice_number) {
        usableTotal += t;
        const sup = i.supplier;
        if (!supplierMap.has(sup)) supplierMap.set(sup, { count: 0, total: 0 });
        const m = supplierMap.get(sup);
        m.count++;
        m.total += t;
      }
      if (Number(i.gst_amount || 0) + Number(i.pst_amount || 0) > 0) withTax++;
      if (i.tax_exempt) taxExempt++;
    }
    const topSuppliers = [...supplierMap.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([name, v]) => ({ name, ...v }));
    return { byStatus, byStatus$, topSuppliers, usableTotal, withTax, taxExempt };
  })();

  // ── Filtered + sorted view ────────────────────────────────────────────────
  function applySort(arr) {
    const dir = sortDir === "asc" ? 1 : -1;
    const out = [...arr];
    out.sort((a, b) => {
      let va, vb;
      if (sortKey === "invoice_date" || sortKey === "email_date") {
        va = a[sortKey] || a.email_date || "";
        vb = b[sortKey] || b.email_date || "";
      } else if (sortKey === "total" || sortKey === "subtotal" || sortKey === "gst_amount" || sortKey === "pst_amount") {
        va = Number(a[sortKey] || 0);
        vb = Number(b[sortKey] || 0);
      } else {
        va = (a[sortKey] || "").toString().toLowerCase();
        vb = (b[sortKey] || "").toString().toLowerCase();
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    return out;
  }
  const searchLower = searchTerm.trim().toLowerCase();
  const matchesSearch = i => {
    if (!searchLower) return true;
    return (
      (i.supplier        || "").toLowerCase().includes(searchLower) ||
      (i.invoice_number  || "").toString().toLowerCase().includes(searchLower) ||
      (i.po_number       || "").toString().toLowerCase().includes(searchLower) ||
      (i.email_subject   || "").toLowerCase().includes(searchLower) ||
      (i.qb_account_name || "").toLowerCase().includes(searchLower) ||
      (i.tech_name       || "").toLowerCase().includes(searchLower)
    );
  };
  const filtered = applySort(
    invoices
      .filter(i => filter === "all" ? true : i.match_status === filter)
      .filter(matchesSearch)
  );

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }
  function SortableTh({ k, label, num }) {
    const active = sortKey === k;
    return (
      <th onClick={() => toggleSort(k)}
        style={{
          textAlign: num ? "right" : "left", padding: "8px 10px",
          fontSize: 11, color: active ? "#a5ce3a" : "#9ca3af",
          fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer",
          userSelect: "none",
        }}>
        {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  }

  const counts = invoices.reduce((acc, i) => {
    acc[i.match_status] = (acc[i.match_status] || 0) + 1;
    return acc;
  }, {});
  const reds = invoices.filter(i => i.match_status === "red");

  // Computed: are we OK to bulk-push? Rule: no oranges among selected (or
  // among all reds if user didn't manually select). This stops accidental
  // push of close matches that probably already exist in QB.
  const candidateIds = selectedIds.size > 0
    ? [...selectedIds]
    : reds.map(i => i.id);
  const candidateRows = invoices.filter(i => candidateIds.includes(i.id));
  const hasOrangesInCandidates = candidateRows.some(i => i.match_status === "orange");

  // ── Render helpers ────────────────────────────────────────────────────────
  function StatusPill({ status, confidence, reason }) {
    const m = STATUS_META[status] || STATUS_META.pending;
    return (
      <span title={reason || ""} style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "rgba(255,255,255,0.04)", color: m.color,
        border: `1px solid ${m.color}40`, borderRadius: 6,
        padding: "2px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
      }}>
        <span>{m.icon}</span>{m.label}
        {confidence != null && status === "orange" ? (
          <span style={{ opacity: 0.7, fontWeight: 400 }}>{Math.round((confidence || 0) * 100)}%</span>
        ) : null}
      </span>
    );
  }

  function RunCard({ run }) {
    const isActive = run.id === activeRunId;
    return (
      <div onClick={() => setActiveRunId(run.id)} style={{
        background: isActive ? "rgba(165,206,58,0.08)" : "rgba(255,255,255,0.03)",
        border: isActive ? "1px solid #a5ce3a" : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 8, padding: "10px 14px", cursor: "pointer",
        minWidth: 200, flexShrink: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f0f0f0" }}>Run #{run.id}</div>
          <div style={{ fontSize: 10, color: STATUS_META[run.status]?.color || "#9ca3af", textTransform: "uppercase" }}>
            {run.status}
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
          {fmtDate(run.start_date)} → {fmtDate(run.end_date)}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
          {run.processed_count}/{run.total_emails} processed
          {run.error_count > 0 ? ` · ${run.error_count} err` : ""}
        </div>
      </div>
    );
  }

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#0d0f12", minHeight: "100vh", padding: "20px 24px 80px", color: "#f0f0f0", fontFamily: "'Roboto', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: "#f0f0f0", fontWeight: 700 }}>QuickBooks Audit</h2>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 12, maxWidth: 720 }}>
            Re-extracts AP emails for a chosen window and matches them to QuickBooks Bills.
            Standalone from Expenses — these rows never enter the production pipeline.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={syncPendingStatus}
            disabled={!!busy}
            title="Mirror qb_published / Bill # from audit-pushed rows back to the Expenses tab. Safe to re-run."
            style={btn("rgba(96,165,250,0.12)", "#60a5fa", "1px solid rgba(96,165,250,0.3)", true)}
          >
            {busy === "sync-pending" ? "Syncing…" : "Sync to Expenses tab"}
          </button>
          <button
            onClick={() => setShowNewRun(v => !v)}
            disabled={!!busy}
            style={btn(showNewRun ? "#374151" : "#a5ce3a", showNewRun ? "#f0f0f0" : "#0d0f12")}
          >
            {showNewRun ? "Close" : "+ New Run"}
          </button>
          {activeRun && (
            <button
              onClick={() => deleteRun(activeRun.id)}
              disabled={!!busy}
              style={btn("rgba(239,68,68,0.12)", "#ef4444", "1px solid rgba(239,68,68,0.3)")}
            >
              Delete Run #{activeRun.id}
            </button>
          )}
        </div>
      </div>

      {/* Sync result panel */}
      {syncResult && (
        <div style={{
          ...card(),
          border: `1px solid ${syncResult.patched > 0 ? "rgba(34,197,94,0.3)" : "rgba(96,165,250,0.3)"}`,
          background: syncResult.patched > 0 ? "rgba(34,197,94,0.06)" : "rgba(96,165,250,0.06)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: syncResult.patched > 0 ? "#86efac" : "#93c5fd" }}>
              {syncResult.patched > 0
                ? `✓ Synced ${syncResult.patched} pending invoice(s) to the Expenses tab`
                : "✓ Expenses tab already reflects every audit-pushed invoice"}
            </div>
            <button
              onClick={() => setSyncResult(null)}
              style={{ background: "transparent", color: "#9ca3af", border: "none", cursor: "pointer", fontSize: 12 }}
            >
              Dismiss
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.6 }}>
            Audit rows already in QB: <strong style={{ color: "#e0e0e0" }}>{syncResult.audit_in_qb}</strong>
            {" "}(<strong style={{ color: "#e0e0e0" }}>{syncResult.audit_pushed_count ?? 0}</strong> pushed
            {" + "}<strong style={{ color: "#e0e0e0" }}>{syncResult.audit_green_count ?? 0}</strong> matched green)
            {" · "}Pending unpublished before sync: <strong style={{ color: "#e0e0e0" }}>{syncResult.pending_unpublished_before}</strong>
            {" · "}Patched: <strong style={{ color: "#e0e0e0" }}>{syncResult.patched}</strong>
            {syncResult.no_match > 0 && (
              <>{" · "}No-match (audit row had no pending sibling): <strong style={{ color: "#e0e0e0" }}>{syncResult.no_match}</strong></>
            )}
            {syncResult.no_bill_id > 0 && (
              <>{" · "}No bill ID on green match: <strong style={{ color: "#e0e0e0" }}>{syncResult.no_bill_id}</strong></>
            )}
          </div>
        </div>
      )}

      {/* New run form */}
      {showNewRun && (
        <div style={card()}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Pick a date range</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>From</label>
            <input type="date" value={newRunStart} onChange={e => setNewRunStart(e.target.value)} style={input()} />
            <label style={{ fontSize: 12, color: "#9ca3af" }}>To</label>
            <input type="date" value={newRunEnd} onChange={e => setNewRunEnd(e.target.value)} style={input()} />
            <button onClick={() => startRun(newRunStart, newRunEnd)} disabled={!!busy} style={btn("#a5ce3a", "#0d0f12")}>
              {busy === "starting" ? "Starting…" : "Start full range"}
            </button>
          </div>

          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>Or run by week:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {weekRanges(newRunStart, newRunEnd).map((w, i) => (
              <button key={i} onClick={() => startRun(w.start, w.end)} disabled={!!busy}
                style={{
                  background: "rgba(255,255,255,0.04)", color: "#f0f0f0",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6,
                  padding: "5px 10px", fontSize: 11, cursor: busy ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}>
                {fmtDate(w.start)} → {fmtDate(w.end)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Run history strip */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Runs</div>
        {loadingRuns ? (
          <div style={{ color: "#6b7280", fontSize: 12 }}>Loading runs…</div>
        ) : runs.length === 0 ? (
          <div style={{ color: "#6b7280", fontSize: 12 }}>No runs yet. Click <strong>+ New Run</strong> to start.</div>
        ) : (
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {runs.map(r => <RunCard key={r.id} run={r} />)}
          </div>
        )}
      </div>

      {/* Active run controls */}
      {activeRun && (
        <div style={card()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                Run #{activeRun.id} — {fmtDate(activeRun.start_date)} → {fmtDate(activeRun.end_date)}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                {activeRun.processed_count}/{activeRun.total_emails} processed · {activeRun.error_count} error(s) · status: <span style={{ color: STATUS_META[activeRun.status]?.color || "#9ca3af" }}>{activeRun.status}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {/* Continue extraction */}
              {(activeRun.status === "extracting" || (activeRun.status === "paused" && (activeRun.pending_message_ids?.length || 0) > 0)) && (
                <button onClick={() => runChunk(activeRun.id)} disabled={!!busy} style={btn("#a5ce3a", "#0d0f12")}>
                  {busy === "extracting" ? "Running…" : "Continue extraction"}
                </button>
              )}
              {busy === "extracting" && (
                <button onClick={() => { cancelRef.current = true; }} style={btn("rgba(245,158,11,0.12)", "#f59e0b", "1px solid rgba(245,158,11,0.3)")}>Pause</button>
              )}
              {/* Match step */}
              {activeRun.status === "extracted" && (
                <button onClick={() => matchRun(activeRun.id)} disabled={!!busy} style={btn("#60a5fa", "#0d0f12")}>
                  {busy === "matching" ? "Matching…" : "Match to QuickBooks"}
                </button>
              )}
              {/* Retry failed */}
              {(activeRun.error_count > 0 || invoices.some(i => i.match_status === "error")) && (
                <button onClick={() => retryFailed(activeRun.id)} disabled={!!busy} style={btn("rgba(167,139,250,0.12)", "#a78bfa", "1px solid rgba(167,139,250,0.3)")}>
                  Retry failed
                </button>
              )}
              {/* Re-match */}
              {(activeRun.status === "matched" || activeRun.status === "extracted") && (
                <button onClick={() => matchRun(activeRun.id)} disabled={!!busy} style={btn("rgba(96,165,250,0.12)", "#60a5fa", "1px solid rgba(96,165,250,0.3)")}>
                  Re-match
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {chunkProgress && (
            <div style={{ marginTop: 4, marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
                Extracted {chunkProgress.processed} so far · {chunkProgress.remaining} remaining
                {chunkProgress.errors > 0 && ` · ${chunkProgress.errors} error(s)`}
              </div>
              <div style={{ height: 6, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${(chunkProgress.processed / Math.max(1, chunkProgress.processed + chunkProgress.remaining)) * 100}%`,
                  background: "#a5ce3a", transition: "width 0.3s",
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status / error toasts */}
      {statusMsg && (
        <div style={{ ...card(), background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.3)", color: "#86efac" }}>
          {statusMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ ...card(), background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5" }}>
          {errorMsg}
          <button onClick={() => setErrorMsg("")} style={{ float: "right", background: "none", border: "none", color: "#fca5a5", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {/* Run stats summary — auto-computed from loaded invoices */}
      {activeRun && stats && (
        <div style={card()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Run summary</div>
            <button onClick={verifyCoverage} disabled={!!busy}
              style={btn("rgba(96,165,250,0.12)", "#60a5fa", "1px solid rgba(96,165,250,0.3)", true)}>
              {busy === "verify" ? "Checking…" : "Verify Gmail coverage"}
            </button>
          </div>

          {/* Per-status $ breakdown */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 12 }}>
            {["green", "orange", "red", "pushed", "error", "pending"].map(s => {
              const meta = STATUS_META[s];
              const c = stats.byStatus[s] || 0;
              if (c === 0) return null;
              return (
                <div key={s} style={{
                  background: `${meta.color}10`, border: `1px solid ${meta.color}30`,
                  borderRadius: 6, padding: "8px 10px",
                }}>
                  <div style={{ fontSize: 10, color: meta.color, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {meta.icon} {meta.label}
                  </div>
                  <div style={{ fontSize: 14, color: "#f0f0f0", marginTop: 2, fontWeight: 600 }}>
                    {c} <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>· {fmtMoney(stats.byStatus$[s])}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {/* Sanity-check totals */}
            <div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Sanity check</div>
              <div style={{ fontSize: 12, color: "#e0e0e0", lineHeight: 1.7 }}>
                <div>Total extracted (usable rows): <strong>{fmtMoney(stats.usableTotal)}</strong></div>
                <div>
                  Already in QB (🟢 green + ⬆ pushed): <strong style={{ color: "#22c55e" }}>
                    {fmtMoney((stats.byStatus$.green || 0) + (stats.byStatus$.pushed || 0))}
                  </strong>
                </div>
                <div>
                  Gap to push (🔴 red): <strong style={{ color: "#ef4444" }}>
                    {fmtMoney(stats.byStatus$.red || 0)}
                  </strong>
                </div>
                <div style={{ color: "#9ca3af", marginTop: 4 }}>
                  Rows with GST/PST extracted: <strong>{stats.withTax}</strong> · tax-exempt: <strong>{stats.taxExempt}</strong>
                </div>
              </div>
            </div>

            {/* Top suppliers */}
            <div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Top 5 suppliers</div>
              <div style={{ fontSize: 12, color: "#e0e0e0" }}>
                {stats.topSuppliers.map(s => (
                  <div key={s.name} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>{s.name}</span>
                    <span style={{ color: "#9ca3af", whiteSpace: "nowrap" }}>{s.count} · {fmtMoney(s.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coverage check result */}
          {coverageInfo && (
            <div style={{
              marginTop: 12, padding: 10, borderRadius: 6,
              background: coverageInfo.truly_missing > 0 ? "rgba(239,68,68,0.06)" : "rgba(34,197,94,0.06)",
              border: `1px solid ${coverageInfo.truly_missing > 0 ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`,
            }}>
              <div style={{ fontSize: 12, color: coverageInfo.truly_missing > 0 ? "#fca5a5" : "#86efac", marginBottom: 6, fontWeight: 600 }}>
                {coverageInfo.truly_missing > 0
                  ? `⚠ ${coverageInfo.truly_missing} email(s) in Gmail were never extracted into this run`
                  : `✓ Coverage verified — every email with attachments in the window is accounted for`}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.6 }}>
                Gmail returned <strong style={{ color: "#e0e0e0" }}>{coverageInfo.gmail_count}</strong> messages with attachments in the run window.
                {" "}<strong style={{ color: "#e0e0e0" }}>{coverageInfo.have_in_this_run}</strong> have rows in this run,
                {" "}<strong style={{ color: "#e0e0e0" }}>{coverageInfo.have_in_other_run}</strong> are covered by an earlier run,
                {" "}<strong style={{ color: "#e0e0e0" }}>{coverageInfo.still_queued}</strong> are still queued.
              </div>
              {coverageInfo.truly_missing > 0 && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => requeueMissing(coverageInfo.missing_message_ids)} disabled={!!busy}
                    style={btn("#ef4444", "#0d0f12", undefined, true)}>
                    Re-extract {coverageInfo.truly_missing} missing email(s)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search + dedupe controls */}
      {activeRun && invoices.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search supplier, invoice #, PO, subject, category…"
            style={{ ...input(), flex: "1 1 280px", minWidth: 220 }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")}
              style={btn("rgba(255,255,255,0.04)", "#9ca3af", "1px solid rgba(255,255,255,0.08)", true)}>
              Clear
            </button>
          )}
          <button onClick={findDuplicates} disabled={!!busy}
            style={btn("rgba(167,139,250,0.12)", "#a78bfa", "1px solid rgba(167,139,250,0.3)")}>
            {busy === "dedupe" ? "Scanning…" : "Find duplicates"}
          </button>
          <button onClick={previewStatementCleanup} disabled={!!busy}
            title="Statements list many already-paid invoices and create bogus audit rows. This finds and removes them."
            style={btn("rgba(245,158,11,0.12)", "#f59e0b", "1px solid rgba(245,158,11,0.3)")}>
            {busy === "statements" ? "Scanning…" : "Remove statement rows"}
          </button>
          <button onClick={previewOrderConfCleanup} disabled={!!busy}
            title="Order confirmations / acknowledgements / sales orders are pre-invoice documents — the real invoice arrives separately. This finds and removes them."
            style={btn("rgba(245,158,11,0.12)", "#f59e0b", "1px solid rgba(245,158,11,0.3)")}>
            {busy === "ordconf" ? "Scanning…" : "Remove order-conf rows"}
          </button>
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            {filtered.length === invoices.length
              ? `${invoices.length} row(s)`
              : `${filtered.length} of ${invoices.length} row(s)`}
          </span>
        </div>
      )}

      {/* Filter pills + bulk actions */}
      {activeRun && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FILTERS.map(f => {
              const meta = f === "all"
                ? { color: "#f0f0f0", icon: "•", label: "All" }
                : STATUS_META[f];
              const c = f === "all" ? invoices.length : (counts[f] || 0);
              const active = filter === f;
              return (
                <button key={f} onClick={() => setFilter(f)}
                  style={{
                    background: active ? `${meta.color}20` : "rgba(255,255,255,0.03)",
                    color: active ? meta.color : "#9ca3af",
                    border: active ? `1px solid ${meta.color}` : "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 999, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600,
                  }}>
                  <span style={{ marginRight: 4 }}>{meta.icon}</span>{meta.label} ({c})
                </button>
              );
            })}
          </div>
          {/* Bulk push */}
          {invoices.length > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {qbAccounts.length > 0 && (
                <select value={defaultAccountId} onChange={e => setDefaultAccountId(e.target.value)}
                  style={{ ...input(), maxWidth: 200, fontSize: 11 }}>
                  <option value="">Default QB account…</option>
                  {qbAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              )}
              <button
                onClick={() => setConfirmBulk(true)}
                disabled={!!busy || candidateIds.length === 0}
                style={btn(
                  candidateIds.length === 0 ? "rgba(255,255,255,0.04)" : "#ef4444",
                  candidateIds.length === 0 ? "#6b7280" : "#0d0f12"
                )}
              >
                Push {selectedIds.size > 0 ? `${selectedIds.size} selected` : `all 🔴 missing (${reds.length})`}
              </button>
              {selectedIds.size > 0 && (
                <button onClick={bulkDeleteSelected} disabled={!!busy}
                  title="Delete the selected audit rows. Already-pushed rows are skipped."
                  style={btn("rgba(239,68,68,0.12)", "#ef4444", "1px solid rgba(239,68,68,0.3)")}>
                  {busy === "deleting" ? "Deleting…" : `Delete ${selectedIds.size}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Invoice table */}
      {activeRun && (
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden" }}>
          {loadingInvoices ? (
            <div style={{ padding: 32, textAlign: "center", color: "#6b7280", fontSize: 12 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#6b7280", fontSize: 12 }}>No rows in this filter.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    <Th><input type="checkbox"
                      checked={filtered.length > 0 && filtered.every(i => selectedIds.has(i.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set([...selectedIds, ...filtered.map(i => i.id)]));
                        else {
                          const next = new Set(selectedIds);
                          filtered.forEach(i => next.delete(i.id));
                          setSelectedIds(next);
                        }
                      }} /></Th>
                    <SortableTh k="match_status"   label="Status" />
                    <SortableTh k="invoice_date"   label="Date" />
                    <SortableTh k="supplier"       label="Supplier" />
                    <SortableTh k="invoice_number" label="Inv #" />
                    <SortableTh k="po_number"      label="PO" />
                    <SortableTh k="subtotal"       label="Subtotal" num />
                    <SortableTh k="gst_amount"     label="GST" num />
                    <SortableTh k="pst_amount"     label="PST" num />
                    <SortableTh k="total"          label="Total" num />
                    <SortableTh k="qb_account_name" label="Category (QB)" />
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id}
                        onClick={() => setDrawerId(inv.id)}
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.04)",
                          cursor: "pointer",
                          background: drawerId === inv.id ? "rgba(165,206,58,0.06)" : "transparent",
                        }}>
                      <Td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(inv.id)}
                          onChange={e => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(inv.id);
                            else next.delete(inv.id);
                            setSelectedIds(next);
                          }} />
                      </Td>
                      <Td><StatusPill status={inv.match_status} confidence={inv.match_confidence} reason={inv.match_reason} /></Td>
                      <Td>{fmtDate(inv.invoice_date || inv.email_date)}</Td>
                      <Td><span title={inv.email_subject || ""}>{inv.supplier || <em style={{ color: "#6b7280" }}>—</em>}</span></Td>
                      <Td>{inv.invoice_number || <em style={{ color: "#6b7280" }}>—</em>}</Td>
                      <Td>{inv.po_number || <em style={{ color: "#6b7280" }}>—</em>}</Td>
                      <Td num>{fmtMoney(inv.subtotal)}</Td>
                      <Td num>{Number(inv.gst_amount) > 0 ? fmtMoney(inv.gst_amount) : <em style={{ color: "#6b7280" }}>—</em>}</Td>
                      <Td num>{Number(inv.pst_amount) > 0 ? fmtMoney(inv.pst_amount) : <em style={{ color: "#6b7280" }}>—</em>}</Td>
                      <Td num style={{ fontWeight: 600 }}>{fmtMoney(inv.total)}</Td>
                      <Td>
                        {inv.qb_account_name ? (
                          <span style={{
                            fontSize: 11, color: "#a5ce3a",
                            background: "rgba(165,206,58,0.08)",
                            border: "1px solid rgba(165,206,58,0.2)",
                            borderRadius: 4, padding: "1px 6px",
                          }} title={`Auto-assigned QB account: ${inv.qb_account_name}`}>
                            {inv.qb_account_name}
                          </span>
                        ) : inv.match_status === "red" ? (
                          <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }} title="Will use the default QB account at the top of the page">
                            (default)
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#6b7280" }}>—</span>
                        )}
                      </Td>
                      <Td onClick={e => e.stopPropagation()}>
                        {inv.qb_published ? (
                          // Negative total = pushed as VendorCredit; route the
                          // deep link to the credit page instead of /app/bill.
                          <a href={qbTxnUrl(Number(inv.total || 0) < 0 ? "VendorCredit" : "Bill", inv.qb_bill_id)}
                             target="_blank" rel="noreferrer"
                             style={{ color: "#60a5fa", fontSize: 11, textDecoration: "none" }}>
                            {Number(inv.total || 0) < 0 ? "Credit" : "Bill"} #{inv.qb_bill_id} ↗
                          </a>
                        ) : inv.match_status === "red" ? (
                          <button onClick={() => pushOne(inv)} disabled={!!busy || !defaultAccountId}
                            style={btn("rgba(34,197,94,0.12)", "#22c55e", "1px solid rgba(34,197,94,0.3)", true)}>
                            Push
                          </button>
                        ) : inv.match_status === "orange" ? (
                          <button onClick={() => {
                            if (!window.confirm(`This is a CLOSE match (${inv.match_reason}). Pushing will create a duplicate Bill in QB if it's already there. Continue?`)) return;
                            pushOne(inv);
                          }} disabled={!!busy || !defaultAccountId}
                            style={btn("rgba(245,158,11,0.12)", "#f59e0b", "1px solid rgba(245,158,11,0.3)", true)}>
                            Push (review)
                          </button>
                        ) : inv.match_status === "error" ? (
                          <span style={{ fontSize: 11, color: "#a78bfa" }}>—</span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#6b7280" }}>—</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Row detail drawer — extracted invoice + PDF preview */}
      {drawerId && (() => {
        const inv = invoices.find(i => i.id === drawerId);
        if (!inv) return null;
        return (
          <div onClick={() => setDrawerId(null)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 90,
            display: "flex", justifyContent: "flex-end",
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              width: "min(960px, 92vw)", height: "100vh",
              background: "#16191f", borderLeft: "1px solid rgba(255,255,255,0.08)",
              overflowY: "auto", padding: "20px 24px",
              boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
            }}>
              {/* Drawer header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <StatusPill status={inv.match_status} confidence={inv.match_confidence} reason={inv.match_reason} />
                    <span style={{ fontSize: 13, color: "#9ca3af" }}>{inv.email_from || ""}</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: 18, color: "#f0f0f0" }}>
                    {inv.supplier || <em style={{ color: "#6b7280" }}>Unknown supplier</em>}
                    {inv.invoice_number ? <span style={{ color: "#9ca3af", fontWeight: 400 }}> · #{inv.invoice_number}</span> : null}
                  </h3>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                    {inv.email_subject || "(no subject)"} · {fmtDate(inv.email_date)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {!inv.qb_published && (
                    <button onClick={() => deleteInvoice(inv.id)} title="Delete this audit row (the Gmail email is unaffected)"
                      style={{
                        background: "rgba(239,68,68,0.10)", color: "#ef4444",
                        border: "1px solid rgba(239,68,68,0.3)",
                        borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                      }}>
                      Delete
                    </button>
                  )}
                  <button onClick={() => setDrawerId(null)} style={{
                    background: "rgba(255,255,255,0.06)", color: "#f0f0f0", border: "none",
                    borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                  }}>Close ✕</button>
                </div>
              </div>

              {/* Key fields */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
                <KV k="Invoice date" v={fmtDate(inv.invoice_date)} />
                <KV k="PO #"         v={inv.po_number || "—"} />
                <KV k="Tech"         v={inv.tech_name || "—"} />
                <KV k="Subtotal"     v={fmtMoney(inv.subtotal)} />
                <KV k="GST"          v={Number(inv.gst_amount) > 0 ? fmtMoney(inv.gst_amount) : (inv.tax_exempt ? "Exempt" : "—")} />
                <KV k="PST"          v={Number(inv.pst_amount) > 0 ? fmtMoney(inv.pst_amount) : "—"} />
                <KV k="Total"        v={fmtMoney(inv.total)} bold />
              </div>

              {/* QB category editor — visible for any row that's pushable */}
              {(inv.match_status === "red" || inv.match_status === "orange" || inv.match_status === "pushed") && (
                <div style={{ ...card(), marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>
                    QuickBooks category
                    {inv.qb_account_name ? (
                      <span style={{ color: "#a5ce3a", marginLeft: 6 }}>(auto-assigned by supplier + line items)</span>
                    ) : (
                      <span style={{ color: "#f59e0b", marginLeft: 6 }}>(no auto-assignment — will use default at push time)</span>
                    )}
                  </div>
                  <select
                    value={inv.qb_account_id || ""}
                    disabled={inv.qb_published || !!busy}
                    onChange={e => {
                      const id = e.target.value;
                      const acct = qbAccounts.find(a => a.id === id);
                      updateCategory(inv.id, id || null, acct?.name || null);
                    }}
                    style={{ ...input(), width: "100%", fontSize: 12 }}>
                    <option value="">(use default: {qbAccounts.find(a => a.id === defaultAccountId)?.name || "none picked"})</option>
                    {qbAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}{a.account_type ? ` — ${a.account_type}` : ""}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Match info — for red rows we also surface the closest QB
                  Bill candidate prominently (when one was found) and a
                  one-click "Mark as already in QB" override. */}
              {(inv.match_reason || inv.qb_bill_match) && (
                <div style={{ ...card(), marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Match against QuickBooks</div>
                  <div style={{ fontSize: 13, color: "#e0e0e0" }}>{inv.match_reason || "—"}</div>
                  {inv.qb_bill_match && (
                    <div style={{
                      marginTop: 10, padding: "10px 12px", borderRadius: 6,
                      background: inv.match_status === "red" ? "rgba(239,68,68,0.06)" : "rgba(96,165,250,0.06)",
                      border: `1px solid ${inv.match_status === "red" ? "rgba(239,68,68,0.25)" : "rgba(96,165,250,0.25)"}`,
                    }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4, fontWeight: 600 }}>
                        {(() => {
                          const t = inv.qb_bill_match._qbTxnType === "VendorCredit" ? "Supplier Credit" : "Bill";
                          return inv.match_status === "red"
                            ? `Closest QB ${t} found (score too low to auto-match)`
                            : `Matched QB ${t}`;
                        })()}
                      </div>
                      <div style={{ fontSize: 12, color: "#e0e0e0", lineHeight: 1.6 }}>
                        Type: <strong>{inv.qb_bill_match._qbTxnType === "VendorCredit" ? "Supplier Credit" : "Bill"}</strong>
                        {" · "}DocNumber: <strong>{inv.qb_bill_match.DocNumber || "(none)"}</strong>
                        {" · "}Total: <strong>{fmtMoney(inv.qb_bill_match.TotalAmt)}</strong>
                        {" · "}Date: <strong>{inv.qb_bill_match.TxnDate}</strong>
                        {inv.qb_bill_match.VendorRef?.name ? <><br/>Vendor: <strong>{inv.qb_bill_match.VendorRef.name}</strong></> : null}
                        {inv.qb_bill_match.PrivateNote ? <><br/><span style={{ color: "#9ca3af" }}>Note: {inv.qb_bill_match.PrivateNote}</span></> : null}
                      </div>
                      {inv.match_status === "red" && (
                        <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <a href={qbTxnUrl(inv.qb_bill_match._qbTxnType, inv.qb_bill_match.Id)}
                             target="_blank" rel="noreferrer"
                             style={{ fontSize: 11, color: "#60a5fa", textDecoration: "none" }}>
                            Open {inv.qb_bill_match._qbTxnType === "VendorCredit" ? "Credit" : "Bill"} #{inv.qb_bill_match.Id} in QB ↗
                          </a>
                          <span style={{ color: "#6b7280", fontSize: 11 }}>·</span>
                          <button onClick={() => markInQb(inv, inv.qb_bill_match.Id)}
                            style={btn("rgba(96,165,250,0.12)", "#60a5fa", "1px solid rgba(96,165,250,0.3)", true)}>
                            ✓ Yes, this is the same — mark as in QB
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Even with no candidate found, let the user manually
                      mark a row as already-in-QB if they know it is. */}
                  {inv.match_status === "red" && !inv.qb_bill_match && (
                    <div style={{ marginTop: 10 }}>
                      <button onClick={() => markInQb(inv, null)}
                        style={btn("rgba(96,165,250,0.12)", "#60a5fa", "1px solid rgba(96,165,250,0.3)", true)}>
                        ✓ I checked — it's already in QB (mark as pushed)
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Line items */}
              {Array.isArray(inv.line_items) && inv.line_items.length > 0 && (
                <div style={{ ...card(), marginBottom: 14, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>Line items ({inv.line_items.length})</div>
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: "#9ca3af" }}>
                        <th style={{ textAlign: "left",  padding: "4px 6px" }}>Description</th>
                        <th style={{ textAlign: "left",  padding: "4px 6px" }}>SKU</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Qty</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Unit</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inv.line_items.map((li, i) => (
                        <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", color: "#e0e0e0" }}>
                          <td style={{ padding: "4px 6px", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>{li.description || "—"}</td>
                          <td style={{ padding: "4px 6px", color: "#9ca3af" }}>{li.sku || "—"}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{li.qty || "—"}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{li.unitCost ? fmtMoney(li.unitCost) : "—"}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600 }}>{li.total ? fmtMoney(li.total) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* PDF preview */}
              {inv.receipt_url ? (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>PDF preview</div>
                    <a href={inv.receipt_url} target="_blank" rel="noreferrer"
                       style={{ fontSize: 11, color: "#60a5fa", textDecoration: "none" }}>
                      Open in new tab ↗
                    </a>
                  </div>
                  <iframe
                    title={`PDF preview ${inv.id}`}
                    src={inv.receipt_url}
                    style={{ width: "100%", height: 720, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, background: "#0d0f12" }}
                  />
                </div>
              ) : (
                <div style={{ ...card(), color: "#9ca3af", fontSize: 12 }}>
                  {inv.extraction_error
                    ? `No PDF stored — extraction error: ${inv.extraction_error}`
                    : "No PDF stored for this row."}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Order-confirmation cleanup preview modal */}
      {ocPreview && (
        <div onClick={() => setOcPreview(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#16191f", border: "1px solid rgba(245,158,11,0.3)",
            borderRadius: 12, padding: 24, maxWidth: 720, width: "92%",
            maxHeight: "85vh", overflowY: "auto",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#f0f0f0" }}>
              Found {ocPreview.total_to_delete} order-confirmation row(s) across {ocPreview.groups.length} email(s)
            </h3>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
              These rows came from order confirmations / acknowledgements / sales orders — pre-invoice
              documents that look like invoices but represent an order in progress, not money owed yet.
              The real invoice arrives separately and gets matched on its own. Already-pushed rows are protected.
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, marginBottom: 12 }}>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", color: "#9ca3af" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Source email subject</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Supplier</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {ocPreview.groups.map(g => (
                    <tr key={g.subject} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", color: "#e0e0e0" }}>
                      <td style={{ padding: "6px 8px", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={g.subject}>
                        {g.subject}
                      </td>
                      <td style={{ padding: "6px 8px" }}>{g.sample_supplier || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", color: "#f59e0b", fontWeight: 600 }}>{g.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setOcPreview(null)} style={btn("rgba(255,255,255,0.06)", "#f0f0f0")}>Cancel</button>
              <button onClick={applyOrderConfCleanup} disabled={!!busy} style={btn("#f59e0b", "#0d0f12")}>
                {busy === "ordconf" ? "Removing…" : `Remove ${ocPreview.total_to_delete} row(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statement cleanup preview modal */}
      {stmtPreview && (
        <div onClick={() => setStmtPreview(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#16191f", border: "1px solid rgba(245,158,11,0.3)",
            borderRadius: 12, padding: 24, maxWidth: 720, width: "92%",
            maxHeight: "85vh", overflowY: "auto",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#f0f0f0" }}>
              Found {stmtPreview.total_to_delete} statement-derived row(s) across {stmtPreview.groups.length} email(s)
            </h3>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
              These are line items from supplier statements (monthly summaries listing already-billed
              invoices) that were wrongly extracted as audit rows. They don't represent actual
              individual invoices and shouldn't be in the audit. Already-pushed rows are protected.
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, marginBottom: 12 }}>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", color: "#9ca3af" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Source email subject</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Supplier</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {stmtPreview.groups.map(g => (
                    <tr key={g.subject} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", color: "#e0e0e0" }}>
                      <td style={{ padding: "6px 8px", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={g.subject}>
                        {g.subject}
                      </td>
                      <td style={{ padding: "6px 8px" }}>{g.sample_supplier || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", color: "#f59e0b", fontWeight: 600 }}>{g.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setStmtPreview(null)} style={btn("rgba(255,255,255,0.06)", "#f0f0f0")}>Cancel</button>
              <button onClick={applyStatementCleanup} disabled={!!busy} style={btn("#f59e0b", "#0d0f12")}>
                {busy === "statements" ? "Removing…" : `Remove ${stmtPreview.total_to_delete} row(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dedupe preview modal */}
      {dupePreview && (
        <div onClick={() => setDupePreview(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#16191f", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12, padding: 24, maxWidth: 720, width: "92%",
            maxHeight: "85vh", overflowY: "auto",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#f0f0f0" }}>
              Found {dupePreview.total_groups} duplicate group(s) — {dupePreview.total_to_delete} row(s) to remove
            </h3>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
              Grouped by supplier + invoice # + PO + total. Per group, the row with the most
              line items is kept; the rest are deleted. Already-pushed rows are protected.
            </div>
            <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, marginBottom: 12 }}>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)", color: "#9ca3af" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Supplier</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Inv #</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>PO</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Total</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Copies</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>To delete</th>
                  </tr>
                </thead>
                <tbody>
                  {dupePreview.groups.map(g => (
                    <tr key={g.key} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", color: "#e0e0e0" }}>
                      <td style={{ padding: "6px 8px" }}>{g.supplier}</td>
                      <td style={{ padding: "6px 8px" }}>{g.invoice_number}</td>
                      <td style={{ padding: "6px 8px" }}>{g.po_number || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmtMoney(g.total)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>{g.members.length}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center", color: "#a78bfa" }}>{g.delete_ids.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDupePreview(null)} style={btn("rgba(255,255,255,0.06)", "#f0f0f0")}>Cancel</button>
              <button onClick={() => applyDedupe(dupePreview.groups.flatMap(g => g.delete_ids))}
                disabled={!!busy} style={btn("#a78bfa", "#0d0f12")}>
                {busy === "dedupe" ? "Removing…" : `Remove ${dupePreview.total_to_delete} duplicate(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk push confirm dialog */}
      {confirmBulk && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
        }} onClick={() => setConfirmBulk(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#16191f", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12, padding: 24, maxWidth: 440, width: "90%",
          }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#f0f0f0" }}>Push {candidateIds.length} invoice(s) to QuickBooks?</h3>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
              Total: <strong style={{ color: "#f0f0f0" }}>{fmtMoney(candidateRows.reduce((s, i) => s + Number(i.total || 0), 0))}</strong>
            </div>
            {hasOrangesInCandidates && (
              <div style={{
                background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)",
                borderRadius: 6, padding: 10, fontSize: 11, color: "#f59e0b", marginBottom: 12,
              }}>
                ⚠️ {candidateRows.filter(i => i.match_status === "orange").length} orange (close-match) invoice(s) included.
                Review them individually before bulk pushing — each may already exist in QB.
              </div>
            )}
            {/* Category breakdown — show how many rows already have a QB
                account auto-assigned vs how many will fall through to the
                default. Helps the user verify everything is categorized
                before they fire a bulk push. */}
            {(() => {
              const withCategory = candidateRows.filter(i => i.qb_account_id);
              const withoutCategory = candidateRows.filter(i => !i.qb_account_id);
              const defaultName = qbAccounts.find(a => a.id === defaultAccountId)?.name;
              return (
                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16, lineHeight: 1.6 }}>
                  <div>
                    <strong style={{ color: "#a5ce3a" }}>{withCategory.length}</strong> with auto-assigned category
                    {withCategory.length > 0 && (
                      <span style={{ color: "#6b7280" }}>
                        {" "}({[...new Set(withCategory.map(i => i.qb_account_name))].slice(0, 3).join(", ")}
                        {[...new Set(withCategory.map(i => i.qb_account_name))].length > 3 ? "…" : ""})
                      </span>
                    )}
                  </div>
                  <div>
                    <strong style={{ color: withoutCategory.length > 0 ? "#f59e0b" : "#6b7280" }}>{withoutCategory.length}</strong> will use default
                    {withoutCategory.length > 0 && (
                      <span style={{ color: defaultName ? "#9ca3af" : "#ef4444" }}>
                        {" → "}{defaultName || "no default picked! Set one above before pushing."}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmBulk(false)} style={btn("rgba(255,255,255,0.06)", "#f0f0f0")}>Cancel</button>
              <button onClick={() => pushBulk(candidateIds)} disabled={!!busy || (hasOrangesInCandidates && selectedIds.size === 0)}
                style={btn("#ef4444", "#0d0f12")}>
                {busy === "pushing" ? "Pushing…" : `Push ${candidateIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Style helpers ────────────────────────────────────────────────────────────
function btn(bg, color, border, small) {
  return {
    background: bg, color,
    border: border || "none",
    borderRadius: 6,
    padding: small ? "4px 10px" : "7px 14px",
    fontSize: small ? 11 : 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  };
}
function input() {
  return {
    background: "rgba(255,255,255,0.04)", color: "#f0f0f0",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6,
    padding: "6px 10px", fontSize: 12, fontFamily: "inherit",
  };
}
function card() {
  return {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10, padding: 14, marginBottom: 14,
  };
}
function Th({ children }) {
  return <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: "#9ca3af", fontWeight: 600, whiteSpace: "nowrap" }}>{children}</th>;
}
function Td({ children, num, style, onClick }) {
  return <td onClick={onClick} style={{ padding: "8px 10px", textAlign: num ? "right" : "left", color: "#e0e0e0", whiteSpace: "nowrap", ...(style || {}) }}>{children}</td>;
}
function KV({ k, v, bold }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "6px 10px" }}>
      <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
      <div style={{ fontSize: 13, color: "#f0f0f0", marginTop: 2, fontWeight: bold ? 700 : 500 }}>{v}</div>
    </div>
  );
}
